import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import NativeCameraUpload from '../components/NativeCameraUpload';
import { getCookie } from '../utils/cookies';
import { Camera, LogOut, Users, Info, Upload, Check, X, Download } from 'lucide-react';
import './PhotographerPage.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://live-event-photos.onrender.com/api';

function PhotographerPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  
  const [event, setEvent] = useState(null);
  const [photographer, setPhotographer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCamera, setShowCamera] = useState(false);
  const fileInputRef = React.useRef(null);
  const [uploadStats, setUploadStats] = useState({ photosTaken: 0, totalPhotographers: 0 });
  const [uploadQueue, setUploadQueue] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]); // Store files for manual download
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  
  useEffect(() => {
    // Check for photographer token in localStorage (Android) or cookies (iOS PWA)
    const token = localStorage.getItem('photographerToken') || getCookie('photographerToken');
    if (!token) {
      // No token, redirect to registration
      navigate(`/register/${eventId}?openCamera=true&role=photographer`);
      return;
    }
    
    // Check if we should show iOS install prompt
    const hasSeenPrompt = localStorage.getItem('hasSeenInstallPrompt');
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isInStandaloneMode = window.navigator.standalone || window.matchMedia('(display-mode: standalone)').matches;
    
    // Show prompt if: iOS device + not in PWA mode + hasn't seen it before
    if (isIOS && !isInStandaloneMode && !hasSeenPrompt) {
      setShowInstallPrompt(true);
    }
    
    // Decode token to get photographer info
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      setPhotographer(payload);
      
      // Verify token is for this event
      if (payload.eventId !== eventId) {
        localStorage.removeItem('photographerToken');
        navigate(`/register/${eventId}?openCamera=true&role=photographer`);
        return;
      }
      
      fetchEventInfo();
    } catch (error) {
      console.error('Invalid token:', error);
      localStorage.removeItem('photographerToken');
      navigate(`/register/${eventId}?openCamera=true&role=photographer`);
    }
  }, [eventId, navigate]);
  
  // Process upload queue in background
  useEffect(() => {
    const processQueue = async () => {
      const pendingUploads = uploadQueue.filter(item => item.status === 'pending');
      
      for (const item of pendingUploads) {
        // Mark as uploading
        setUploadQueue(prev => prev.map(i => 
          i.id === item.id ? { ...i, status: 'uploading' } : i
        ));

        try {
          await handleCameraUpload(item.file);
          
          // Mark as uploaded
          setUploadQueue(prev => prev.map(i => 
            i.id === item.id ? { ...i, status: 'uploaded' } : i
          ));
        } catch (err) {
          console.error('Upload error:', err);
          
          // Mark as failed
          setUploadQueue(prev => prev.map(i => 
            i.id === item.id ? { ...i, status: 'failed', error: err.message } : i
          ));
        }
      }
    };

    processQueue();
  }, [uploadQueue]);
  
  const fetchEventInfo = async () => {
    try {
      const response = await fetch(`${API_URL}/events/${eventId}/public`);
      if (response.ok) {
        const data = await response.json();
        setEvent(data.event);
      }
      
      // Fetch photographer-specific stats
      try {
        const token = localStorage.getItem('photographerToken') || getCookie('photographerToken');
        if (!token) return;
        
        const payload = JSON.parse(atob(token.split('.')[1]));
        const photographerId = payload.photographerId;
        
        if (!photographerId) return;
        
        const statsResponse = await fetch(`${API_URL}/events/${eventId}/stats?photographerId=${photographerId}`);
        
        if (statsResponse.ok) {
          const statsData = await statsResponse.json();
          setUploadStats({
            photosTaken: parseInt(statsData.totalPhotos, 10) || 0,
            totalPhotographers: parseInt(statsData.totalUsers, 10) || 0
          });
        }
      } catch (statsError) {
        console.error('Stats error:', statsError);
      }
    } catch (error) {
      console.error('Error fetching event:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleCameraUpload = async (file) => {
    // Check localStorage first (Android), then cookies (iOS PWA)
    const token = localStorage.getItem('photographerToken') || getCookie('photographerToken');
    console.log('📸 Upload token:', token ? `Found (${token.substring(0, 20)}...)` : 'NOT FOUND');
    
    if (!token) {
      alert('No authentication token found. Please re-register.');
      return;
    }
    
    const formData = new FormData();
    formData.append('photos', file);
    
    try {
      const response = await fetch(`${API_URL}/events/${eventId}/upload-photos`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (response.ok) {
        // Update stats - increment photos taken
        setUploadStats(prev => ({
          ...prev,
          photosTaken: prev.photosTaken + 1
        }));
        return true;
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  };
  
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Add files to upload queue (no auto-download)
    const newUploads = files.map((file, index) => ({
      id: Date.now() + index,
      file,
      status: 'pending',
      name: file.name
    }));
    
    setUploadQueue(prev => [...prev, ...newUploads]);

    // Reset input so same file can be selected again
    e.target.value = '';
  };
  
  // Download all uploaded photos to device
  const downloadAllPhotos = () => {
    uploadQueue.filter(item => item.status === 'uploaded').forEach((item, index) => {
      setTimeout(() => {
        const url = URL.createObjectURL(item.file);
        const a = document.createElement('a');
        a.href = url;
        a.download = item.name || `photo_${Date.now()}.jpg`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, index * 500); // Stagger downloads to avoid browser blocking
    });
  };

  const handleLogout = () => {
    localStorage.removeItem('photographerToken');
    navigate(`/register/${eventId}`);
  };
  
  const clearCompletedUploads = () => {
    setUploadQueue(prev => prev.filter(item => 
      item.status === 'pending' || item.status === 'uploading'
    ));
  };
  
  if (loading) {
    return (
      <div className="photographer-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  const uploadingCount = uploadQueue.filter(i => i.status === 'uploading').length;
  const uploadedCount = uploadQueue.filter(i => i.status === 'uploaded').length;
  const failedCount = uploadQueue.filter(i => i.status === 'failed').length;
  const hasUploads = uploadQueue.length > 0;

  return (
    <div className="photographer-page">
      <header className="photographer-header">
        <div className="header-content">
          <div className="logo-container">
            <img src="https://storage.googleapis.com/msgsndr/oNgjqmOcyIwtcMEEfqUj/media/69051e7a306550caec6c2c16.webp" alt="EventPicLive" className="header-logo" />
          </div>
          <button onClick={handleLogout} className="logout-button">
            <LogOut size={18} />
            Logout
          </button>
        </div>
      </header>
      
      <div className="photographer-content">
        <div className="event-info-section">
          <div className="event-title">
            <Camera size={20} className="event-icon" />
            <h1>{event?.name}</h1>
          </div>
          <p className="photographer-name">Welcome, {photographer?.name}</p>
        </div>
        
        <div className="stats-card">
          <div className="stat-item">
            <Camera size={24} className="stat-icon" />
            <div className="stat-info">
              <div className="stat-value">{uploadStats.photosTaken}</div>
              <div className="stat-label">Photos<br />Taken</div>
            </div>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item">
            <Users size={24} className="stat-icon" />
            <div className="stat-info">
              <div className="stat-value">{uploadStats.totalPhotographers}</div>
              <div className="stat-label">Total Photographers</div>
            </div>
          </div>
        </div>
        
        <div className="camera-section">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />
          <button 
            onClick={() => fileInputRef.current?.click()} 
            className="start-camera-button"
          >
            <Camera size={24} />
            Take Photos
          </button>
        </div>
        
        {hasUploads && (
          <div className="upload-status-section">
            <div className="upload-status-header">
              <div className="upload-status-title">
                <Upload size={18} />
                <span>Upload Status</span>
              </div>
              {(uploadedCount > 0 || failedCount > 0) && (
                <button onClick={clearCompletedUploads} className="clear-button">
                  Clear
                </button>
              )}
            </div>
            
            <div className="upload-summary">
              {uploadingCount > 0 && (
                <div className="summary-item uploading">
                  <div className="summary-spinner"></div>
                  <span>{uploadingCount} uploading...</span>
                </div>
              )}
              {uploadedCount > 0 && (
                <div className="summary-item uploaded">
                  <Check size={16} />
                  <span>{uploadedCount} uploaded</span>
                </div>
              )}
              {failedCount > 0 && (
                <div className="summary-item failed">
                  <X size={16} />
                  <span>{failedCount} failed</span>
                </div>
              )}
            </div>
            
            {uploadedCount > 0 && (
              <button onClick={downloadAllPhotos} className="download-photos-button">
                <Download size={18} />
                Save {uploadedCount} Photo{uploadedCount > 1 ? 's' : ''} to Device
              </button>
            )}
          </div>
        )}
        
        <div className="info-section">
          <div className="info-header">
            <Info size={20} />
            <h3>Quick Guide</h3>
          </div>
          <ul className="instructions-list">
            <li>Tap "Take Photos" to open your camera</li>
            <li>Photos are automatically processed with face detection</li>
            <li>Tagged attendees receive instant MMS notifications</li>
            <li>Keep taking photos - no need to wait for uploads!</li>
          </ul>
        </div>
      </div>
      
      {showCamera && (
        <NativeCameraUpload
          eventId={eventId}
          onUpload={handleCameraUpload}
          onClose={() => setShowCamera(false)}
        />
      )}
      
      {/* iOS Install Prompt */}
      {showInstallPrompt && (
        <div className="install-prompt-overlay" onClick={() => {
          setShowInstallPrompt(false);
          localStorage.setItem('hasSeenInstallPrompt', 'true');
        }}>
          <div className="install-prompt-modal" onClick={(e) => e.stopPropagation()}>
            <div className="install-prompt-header">
              <h3>📱 Add to Home Screen</h3>
            </div>
            <div className="install-prompt-content">
              <p>For the best experience, add EventPicLive to your home screen:</p>
              <ol className="install-steps">
                <li>
                  <span className="step-icon">1</span>
                  <span>Tap the <strong>Share</strong> button <svg width="18" height="24" viewBox="0 0 18 24" fill="none" stroke="#667eea" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{display: 'inline-block', verticalAlign: 'middle', margin: '0 4px'}}><path d="M9 15V3M9 3L5 7M9 3L13 7"/><path d="M15 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-9"/></svg> at the bottom</span>
                </li>
                <li>
                  <span className="step-icon">2</span>
                  <span>Scroll down and tap <strong>"Add to Home Screen"</strong></span>
                </li>
                <li>
                  <span className="step-icon">3</span>
                  <span>Tap <strong>"Add"</strong> in the top right</span>
                </li>
              </ol>
              <p className="install-benefit">✨ Access the camera instantly from your home screen!</p>
            </div>
            <button 
              className="install-prompt-button"
              onClick={() => {
                setShowInstallPrompt(false);
                localStorage.setItem('hasSeenInstallPrompt', 'true');
              }}
            >
              Got it!
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default PhotographerPage;
